// /src/pages/PlanosPublicadosPage.jsx
// VERSÃO 7.4 SUPREMA - TIM UNITY COMMAND CENTER (BANNERS LIVE PREVIEW)

import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/layout/MainLayout'; 
import { 
    FiMonitor, FiLoader, FiGrid, FiCpu, FiLayers, FiZap, 
    FiAlertTriangle, FiImage, FiSmartphone, FiPlay, FiX
} from 'react-icons/fi';
import apiClient from '../api/apiClient'; 
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

// Nossos Componentes Atualizados
import CardDestaqueSlideshow from '../components/imovel/CardDestaqueSlideshow'; 
import ImovelCard from '../components/imovel/ImovelCard'; 
import BannerCard from '../components/imovel/BannerCard'; 
import MegaFilterSidebar from '../components/imovel/MegaFilterSidebar'; 
import ImovelEditorModal from '../components/modals/ImovelEditorModal';
import CardDestaquePreview from '../components/imovel/CardDestaquePreview'; 
import BannerPlayer from '../components/imovel/BannerPlayer'; // ✅ UPGRADE: Player de Simulação

export default function PlanosPublicadosPage() {
    const [viewMode, setViewMode] = useState('planos'); 
    const [imoveis, setImoveis] = useState([]); 
    const [banners, setBanners] = useState([]); 
    const [destaques, setDestaques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [lastQuery, setLastQuery] = useState({}); 
    
    // MODAIS
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isEditorModalOpen, setIsEditorModalOpen] = useState(false);
    const [isLivePreviewOpen, setIsLivePreviewOpen] = useState(false); // ✅ UPGRADE: Modal do Player
    const [imovelAlvo, setImovelAlvo] = useState(null);

    // FILTROS (TIM UNITY)
    const [filters, setFilters] = useState({
        tipoPrincipal: 'Ambos',
        nivelCobertura: '',
        cidade: '',
        estado: '',
        minPreco: '',
        maxPreco: '',
        isDestaque: false
    });

    // CAPTURA ROLE DO USUÁRIO
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserRole(decoded.role?.toUpperCase());
            } catch(e) { setUserRole('GUEST'); }
        }
    }, []);

    // FETCH DE PLANOS
    const fetchData = useCallback(async (currentFilters = filters) => {
        setLoading(true);
        try {
            const params = { status: 'Aprovado', ...currentFilters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === 'Ambos') {
                    delete params[key];
                }
            });

            setLastQuery(params); 
            const response = await apiClient.get('/api/filtros/busca', { params });
            const dadosRecebidos = Array.isArray(response.data) ? response.data : [];
            
            setImoveis(dadosRecebidos);
            setDestaques(dadosRecebidos.filter(i => i.isDestaque).slice(0, 3));
            
        } catch (err) {
            toast.error("🚨 Falha ao conectar com o Radar de Ofertas.");
        } finally {
            setLoading(false);
        }
    }, [filters]);

    // FETCH DE BANNERS
    const fetchBanners = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/api/banners/public');
            setBanners(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            toast.error("🚨 Falha ao carregar o Acervo de Banners.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userRole) return;
        if (viewMode === 'planos') fetchData();
        if (viewMode === 'banners') fetchBanners();
    }, [userRole, viewMode, fetchData, fetchBanners]);

    // HANDLERS
    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);
        fetchData(newFilters);
    };

    const handleImovelAction = (idOrObj, action) => {
        const target = typeof idOrObj === 'string' ? imoveis.find(i => i._id === idOrObj) : idOrObj;
        setImovelAlvo(target);
        if (action === 'edit') setIsEditorModalOpen(true);
        if (action === 'previa') setIsPreviewModalOpen(true);
    };

    const handleBannerAction = async (id, action) => {
        if (action === 'delete') {
            if (window.confirm("⚠️ CONFIRMAR INCINERAÇÃO DE MÍDIA?")) {
                try {
                    await apiClient.delete(`/api/banners/${id}`);
                    toast.success("🛡️ MÍDIA REMOVIDA DO ACERVO.");
                    fetchBanners();
                } catch (e) {
                    toast.error("💥 FALHA NA OPERAÇÃO.");
                }
            }
        }
    };

    return (
        <MainLayout>
            <div className="flex h-screen overflow-hidden -m-4 md:-m-8 transition-all duration-500"
                 style={{ backgroundColor: 'var(--bg-main)' }}>
                
                {viewMode === 'planos' && (
                    <MegaFilterSidebar initialFilters={filters} onFilter={handleFilterApply} />
                )}

                <main className="flex-1 overflow-y-auto p-8 md:p-12 custom-scrollbar relative z-10">
                    <header className="flex flex-col xl:flex-row xl:items-center justify-between mb-12 border-b pb-10"
                            style={{ borderColor: 'var(--border-color)' }}>
                        <div>
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 rounded-2xl border flex items-center justify-center text-accent shadow-inner bg-[var(--bg-card)] border-[var(--border-color)]">
                                    {viewMode === 'planos' ? <FiSmartphone size={32} /> : <FiImage size={32} />}
                                </div>
                                <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-auto">
                                    {viewMode === 'planos' ? 'Planos' : 'Banners'} <span className="text-accent">Ativos</span>
                                </h1>
                            </div>
                            <p className="text-[10px] font-black uppercase tracking-[0.5em] mt-4 opacity-40 text-auto ml-20">
                                {viewMode === 'planos' ? 'Monitoramento de Ofertas Corporativas' : 'Gestão de Impacto Visual e Campanhas'}
                            </p>
                        </div>
                        
                        <div className="flex flex-col md:flex-row gap-4 mt-6 xl:mt-0">
                            {/* ✅ UPGRADE: BOTÃO SIMULAR VITRINE (Aparece só no modo Banners) */}
                            {viewMode === 'banners' && banners.length > 0 && (
                                <button 
                                    onClick={() => setIsLivePreviewOpen(true)}
                                    className="px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest bg-white text-black hover:bg-accent transition-all flex items-center gap-3 shadow-2xl"
                                >
                                    <FiPlay fill="currentColor" /> Simular Vitrine
                                </button>
                            )}

                            <div className="flex bg-black/20 p-1.5 rounded-2xl border border-[var(--border-color)]">
                                <button 
                                    onClick={() => setViewMode('planos')}
                                    className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-3 ${viewMode === 'planos' ? 'bg-accent text-black shadow-xl' : 'text-auto opacity-40 hover:opacity-100'}`}
                                >
                                    <FiSmartphone /> Planos
                                </button>
                                <button 
                                    onClick={() => setViewMode('banners')}
                                    className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-3 ${viewMode === 'banners' ? 'bg-accent text-black shadow-xl' : 'text-auto opacity-40 hover:opacity-100'}`}
                                >
                                    <FiImage /> Banners
                                </button>
                            </div>
                        </div>
                    </header>

                    <div className="space-y-20 max-w-[1600px] mx-auto">
                        
                        {viewMode === 'planos' && (
                            <>
                                {destaques.length > 0 && (
                                    <section className="animate-in fade-in zoom-in-95 duration-1000">
                                        <div className="flex items-center gap-3 mb-6 px-2 opacity-50">
                                            <FiZap size={14} className="text-accent" />
                                            <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-auto">Seleção de Ofertas Estratégicas</h2>
                                        </div>
                                        <CardDestaqueSlideshow imovel={destaques[0]} />
                                    </section>
                                )}

                                <section className="pb-40">
                                    <div className="flex items-center justify-between mb-10 border-l-2 border-accent pl-6">
                                        <h2 className="text-xl font-black text-auto uppercase tracking-tighter flex items-center gap-3">
                                            <FiGrid className="text-accent" size={20} /> Grade de Ofertas
                                        </h2>
                                        <span className="text-[10px] font-black uppercase tracking-widest text-auto opacity-40">
                                            {imoveis.length.toString().padStart(2, '0')} Planos Localizados
                                        </span>
                                    </div>

                                    {loading ? <LoadingState /> : imoveis.length === 0 ? <EmptyState query={lastQuery} /> : (
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-10 animate-in fade-in slide-in-from-bottom-8 duration-1000">
                                            {imoveis.map(imovel => (
                                                <ImovelCard key={imovel._id} imovel={imovel} userRole={userRole} onAction={handleImovelAction} />
                                            ))}
                                        </div>
                                    )}
                                </section>
                            </>
                        )}

                        {viewMode === 'banners' && (
                            <section className="pb-40 animate-in fade-in slide-in-from-bottom-4 duration-700">
                                <div className="flex items-center justify-between mb-10 border-l-2 border-accent pl-6">
                                    <h2 className="text-xl font-black text-auto uppercase tracking-tighter flex items-center gap-3">
                                        <FiImage className="text-accent" size={20} /> Acervo de Mídia
                                    </h2>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-auto opacity-40">
                                        {banners.length.toString().padStart(2, '0')} Banners em Exibição
                                    </span>
                                </div>

                                {loading ? <LoadingState /> : banners.length === 0 ? (
                                    <div className="py-20 text-center border-2 border-dashed border-[var(--border-color)] rounded-[4rem] bg-[var(--bg-card)] opacity-60">
                                        <FiLayers className="mx-auto mb-6 text-auto opacity-20" size={60} />
                                        <h3 className="text-2xl font-black text-auto uppercase tracking-tighter">Acervo Vazio</h3>
                                        <p className="text-[10px] font-black uppercase tracking-[0.4em] mt-4 text-accent">Nenhum banner foi publicado no sistema.</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
                                        {banners.map(banner => (
                                            <BannerCard 
                                                key={banner._id} 
                                                banner={banner} 
                                                isAdmin={userRole === 'MASTER' || userRole === 'ADM'} 
                                                onAction={handleBannerAction} 
                                            />
                                        ))}
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </main>
            </div>

            {/* MODAIS DE COMANDO */}

            {/* ✅ UPGRADE: MODAL LIVE PLAYER (A VISÃO DO SITE) */}
            {isLivePreviewOpen && (
                <div className="fixed inset-0 z-[2000] flex flex-col bg-[var(--bg-main)] animate-in fade-in zoom-in-95 duration-500 overflow-hidden">
                    <header className="p-6 border-b border-[var(--border-color)] flex justify-between items-center bg-[var(--bg-card)]">
                        <div className="flex items-center gap-4">
                            <FiZap className="text-accent animate-pulse" />
                            <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-auto">Simulação de Vitrine Pública</h3>
                        </div>
                        <button 
                            onClick={() => setIsLivePreviewOpen(false)}
                            className="p-3 rounded-full hover:bg-red-500/10 text-red-500 transition-all border border-red-500/20"
                        >
                            <FiX size={20} />
                        </button>
                    </header>
                    <div className="flex-1 overflow-y-auto no-scrollbar">
                        {/* Simula o Topo do Site */}
                        <div className="p-4 md:p-10">
                            <div className="rounded-[3rem] overflow-hidden border border-[var(--border-color)] shadow-2xl">
                                <BannerPlayer posicao="home_topo" />
                            </div>
                            <div className="mt-12 text-center opacity-20">
                                <p className="text-[9px] font-black uppercase tracking-[1em]">Simulação de Fluxo de Navegação</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Detalhes do Plano */}
            {isPreviewModalOpen && imovelAlvo && (
                <div className="fixed inset-0 z-[1000] flex justify-center items-center p-4 md:p-8 bg-black/80 backdrop-blur-md overflow-hidden animate-in fade-in duration-300">
                    <CardDestaquePreview imovel={imovelAlvo} onClose={() => setIsPreviewModalOpen(false)} />
                </div>
            )}

            {/* Modal de Edição */}
            {isEditorModalOpen && imovelAlvo && (
                <ImovelEditorModal imovelId={imovelAlvo._id} onClose={() => setIsEditorModalOpen(false)} onUpdateSuccess={() => fetchData()} />
            )}
        </MainLayout>
    );
}

const LoadingState = () => (
    <div className="py-48 text-center flex flex-col items-center justify-center border-2 border-dashed border-[var(--border-color)] rounded-[4rem] bg-[var(--bg-card)] transition-all">
        <div className="relative">
            <FiCpu className="animate-spin mb-8 text-accent opacity-20" size={80} />
            <FiLoader className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-spin-reverse text-accent" size={40} />
        </div>
        <p className="text-[11px] font-black uppercase tracking-[0.6em] text-auto opacity-40">Sincronizando Radar...</p>
    </div>
);

const EmptyState = ({ query }) => (
    <div className="py-20 text-center border-2 border-dashed border-[var(--border-color)] rounded-[4rem] bg-[var(--bg-card)] opacity-60">
        <FiLayers className="mx-auto mb-6 text-auto opacity-20" size={60} />
        <h3 className="text-2xl font-black text-auto uppercase tracking-tighter">Sinal Perdido</h3>
        <p className="text-[10px] font-black uppercase tracking-[0.4em] mt-4 text-accent">Nenhum resultado corresponde aos filtros aplicados.</p>
        <div className="mt-12 inline-block text-left bg-black/20 p-6 rounded-2xl border border-white/5">
            <h4 className="text-[9px] font-black uppercase tracking-widest text-white/50 mb-3 flex items-center gap-2">
                <FiAlertTriangle className="text-amber-500" /> API Query
            </h4>
            <pre className="text-[10px] text-accent font-mono bg-black/40 p-4 rounded-xl">{JSON.stringify(query, null, 2)}</pre>
        </div>
    </div>
);